import i18n from "../../../i18n";
import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { BarChart3, Clock, AlertTriangle } from "lucide-react";
import { AppLayout, PageBackLink } from "../../../components/layout";
import { PageHeader, LoadingState, EmptyState } from "../../../components/ui";
import { cn } from "../../../lib/utils";
import {
  fetchEmployeePolls,
  effectiveStatus,
  canViewResults,
  hasVoted,
} from "../../../services/pollServices";
import { TablePagination } from "../../leave-management/components/TablePagination";
import { StatusPill } from "../components/StatusPill";
import { Toast } from "../components/Toast";
import { useOnline, OfflineBanner } from "../hooks/useOnline";
import {
  ROUTES,
  RESPONSE_TYPE_COLOR,
  formatDateTime,
  timeRemaining,
  isUrgent,
} from "../constants/pollOptions";

const PAGE_SIZE = 10;

function TabPill({ tab, active, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "inline-flex items-center gap-2 min-h-[48px] h-11 px-5 rounded-xl text-sm font-semibold transition cursor-pointer",
        active
          ? "bg-primary text-white"
          : "per-file-hint hover:bg-white/70 hover:text-foreground"
      )}>

      {tab.label}
      {tab.showCount && tab.count > 0 && (
        <span
          className="grid place-items-center h-5 min-w-5 px-1 rounded-full text-[11px] font-bold text-white"
          style={{ backgroundColor: active ? "var(--status-published-dot)" : "var(--status-published-fg)" }}>
          {tab.count}
        </span>
      )}
    </button>
  );
}

/**
 * One card per poll, with one action button.
 *
 * <p>Active → Vote Now. Completed → View Results only when this viewer may see
 * them, otherwise View Response so they can still re-read their own answer.
 */
function PollCard({ poll, tab, online, onVote, onViewResults }) {
  const status = effectiveStatus(poll);
  const barColor = RESPONSE_TYPE_COLOR[poll.responseType] ?? "var(--color-accent)";
  const urgent = tab === "active" && isUrgent(poll.endDateTime);
  // The poll's results settings decide this; HR and the creator keep access.
  const resultsOpen = canViewResults(poll);
  const votedByMe = hasVoted(poll);

  let action = null;
  if (tab === "active") {
    action = {
      label: online ? "Vote Now" : "Offline — can't vote",
      onClick: onVote,
      primary: true,
      disabled: !online,
    };
  } else if (resultsOpen) {
    action = { label: "View Results", onClick: onViewResults, primary: false };
  } else if (votedByMe) {
    action = { label: "View Response", onClick: onVote, primary: false };
  }

  return (
    <div
      className="bg-white rounded-xl px-5 py-4 border-l-4 shadow-[0_1px_4px_rgba(0,0,0,0.06)] hover:shadow-[0_4px_14px_rgba(30,51,131,0.12)] transition"
      style={{ borderLeftColor: barColor }}>

      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <h3 className="text-[16px] font-semibold text-foreground wrap-break-word">{poll.pollTitle}</h3>
          <p className="mt-1 text-[13px] per-file-hint line-clamp-2 wrap-break-word">
            {poll.pollQuestion}
          </p>
        </div>
        <StatusPill status={tab === "completed" ? "Completed" : status} />
      </div>

      <div className="mt-3 flex items-center justify-between gap-3 flex-wrap">
        <span className="inline-flex items-center gap-1.5 text-[12px] per-file-hint">
          <Clock className="h-3.5 w-3.5" />
          {status === "Closed" ? "Closed " : "Closes "}
          {formatDateTime(poll.endDateTime)}
        </span>

        {urgent && (
          <span className="inline-flex items-center gap-1.5 rounded-full bg-(--status-warning-bg) px-2.5 py-1 text-[12px] font-bold text-(--status-warning-fg)">
            <AlertTriangle className="h-3.5 w-3.5" />
            {timeRemaining(poll.endDateTime)}
          </span>
        )}

        {action && (
          <button
            type="button"
            onClick={action.onClick}
            disabled={action.disabled}
            className={cn(
              "ml-auto min-h-[48px] h-11 px-5 rounded-xl text-sm font-semibold transition disabled:opacity-50 disabled:cursor-not-allowed",
              action.primary
                ? "bg-primary text-white hover:bg-accent"
                : "border border-(--color-text-border) bg-white text-accent hover:bg-workspace"
            )}>
            {action.label}
          </button>
        )}
      </div>
    </div>
  );
}

/**
 * Screen 7 — Poll Dashboard.
 * Two tabs only: Active (open, soonest closing first) and Completed. A poll
 * lands in Completed once the employee votes OR once it closes without their
 * vote — there is no separate Closed tab.
 */
export function PollDashboardPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const online = useOnline();
  const [data, setData] = useState({ active: [], completed: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // VotePollPage can hand over which tab to open (it sends "completed" after a
  // vote so the poll it just moved is visible), plus a one-shot confirmation.
  const [activeTab, setActiveTab] = useState(
    location.state?.tab === "completed" ? "completed" : "active"
  );
  const [toast, setToast] = useState(location.state?.toast ?? "");
  const [page, setPage] = useState(1);

  useEffect(() => {
    fetchEmployeePolls()
      .then(setData)
      .catch((err) => setError(err.message || "Failed to load polls."))
      .finally(() => setLoading(false));
  }, []);

  // Drop the handed-over flags from history state so they don't fire again on
  // a refresh or a back/forward navigation.
  useEffect(() => {
    if (location.state?.toast || location.state?.tab) {
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location, navigate]);

  useEffect(() => {
    setPage(1);
  }, [activeTab]);

  const tabs = [
    { key: "active", label: "Active", count: data.active.length, showCount: true },
    { key: "completed", label: "Completed", count: data.completed.length, showCount: false },
  ];

  const tabItems = data[activeTab] ?? [];
  const totalPages = Math.max(Math.ceil(tabItems.length / PAGE_SIZE), 1);
  const safePage = Math.min(page, totalPages);
  const visible = tabItems.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

  return (
    <AppLayout>
      <PageHeader back={<PageBackLink to="/" />} title={i18n.t("polls.polls")} />

      {!online && <OfflineBanner />}

      <div className="flex gap-2.5 justify-center flex-wrap mb-6">
        {tabs.map((tab) => (
          <TabPill key={tab.key} tab={tab} active={activeTab === tab.key} onClick={() => setActiveTab(tab.key)} />
        ))}
      </div>

      <div className="max-w-[960px] mx-auto flex flex-col gap-2.5">
        {loading ? (
          <LoadingState message="Loading polls…" />
        ) : error ? (
          <div className="p-8 text-center text-sm text-error">{error}</div>
        ) : tabItems.length === 0 ? (
          <EmptyState
            icon={BarChart3}
            title={i18n.t("polls.nothingHereYet")}
            message={
              activeTab === "active"
                ? "You have no polls waiting for your vote."
                : "Polls you have voted on, or that closed, will appear here."
            } />
        ) : (
          <>
            {visible.map((poll) => (
              <PollCard
                key={poll.pollId}
                poll={poll}
                tab={activeTab}
                online={online}
                onVote={() => navigate(ROUTES.essVote(poll.pollId))}
                onViewResults={() => navigate(ROUTES.essResults(poll.pollId))} />
            ))}
            <div className="mt-4">
              <TablePagination
                page={safePage}
                totalPages={totalPages}
                totalItems={tabItems.length}
                pageSize={PAGE_SIZE}
                onPageChange={setPage} />
            </div>
          </>
        )}
      </div>

      {toast && <Toast tone="success" onDone={() => setToast("")}>✓ {toast}</Toast>}
    </AppLayout>
  );
}
